import React, { useEffect, useState } from 'react';

import searchTokens from '@amzn/meridian-tokens/base/icon/search';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import BaliOrDaliOption from 'src/components/pages/newJMR/backgroundPage/associateInformation/caseSource/baliOrDaliOption';
import CaseSelection from 'src/components/shared/caseSelection/caseSelection';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import {
  fetchAustinCases,
  getAccommodationKeys,
  getAccommodationStatusForAccommodation,
  getEmployeeInfo,
  getPlacementDetails,
  setAssociateInfoFormData,
  setIsEditAssociateInfoModalOpen,
  setIsEditAssociateInfoSaving,
  updateAssociateInformation,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { getExampleCaseIdText } from 'src/utils/helpers';
import { CaseSources } from 'src/utils/jmrUtils';

import './editAssociateInfoModal.scss';

const EditAssociateInfoModalAUSTIN = () => {
  const dispatch = useAppDispatch();
  const {
    isEditAssociateInfoModalOpen,
    editAssociateInfoModalFormData,
    placementId,
    isEditAssociateInfoSaving,
    placementDetails,
    austinCases,
  } = useAppSelector((state) => state.placementDetailsPage);
  const { featureFlags } = useAppSelector((state) => state.user);
  const [manuallyEnterCaseId, setManuallyEnterCaseId] = useState(false);

  const handleChange = (key: string, val: string) =>
    dispatch(setAssociateInfoFormData({ ...editAssociateInfoModalFormData, [key]: val }));

  const toggleManuallyEnterCaseId = () => setManuallyEnterCaseId(!manuallyEnterCaseId);

  const saveAndClose = async () => {
    dispatch(setIsEditAssociateInfoSaving(true));
    const updateRequest: any = await dispatch(updateAssociateInformation());
    if (updateRequest.payload !== undefined && updateRequest.payload.status === 200) {
      dispatch(getPlacementDetails(+placementId));
      if (featureFlags.DECISION_PORTAL) {
        dispatch(getAccommodationStatusForAccommodation(+placementId));
      }
      dispatch(getAccommodationKeys(+placementId));
      dispatch(setIsEditAssociateInfoModalOpen(false));
    }
    dispatch(setIsEditAssociateInfoSaving(false));
  };

  const isAnyFormDataAbsent = () => {
    const d = editAssociateInfoModalFormData;
    return [d.login, d.fullName, d.employeeId, d.job, d.homeShift, d.caseId, d.caseSource].some(
      (field) => !field?.trim(),
    );
  };

  useEffect(() => {
    if (isEditAssociateInfoModalOpen) {
      dispatch(fetchAustinCases({ login: placementDetails.login, placementId: +placementId }));
    }
  }, [isEditAssociateInfoModalOpen]);

  return (
    <Modal
      title="Edit Associate Information"
      open={isEditAssociateInfoModalOpen}
      onClose={() => dispatch(setIsEditAssociateInfoModalOpen(false))}
      width="580px"
      bodySpacingInset="500"
    >
      <Column spacing="400">
        <Text>
          You can edit the associate's information here. Once done, please click the confirm button to save these
          changes.
        </Text>
        {!isEditAssociateInfoSaving ? (
          <>
            <Row widths={['grid-3', 'grid-8']}>
              <Text tag="label" type="b300" color="primary">
                Login
              </Text>
              <Input
                type="text"
                placeholder="Associate's alias"
                value={editAssociateInfoModalFormData.login}
                onChange={(newLogin) => handleChange('login', newLogin)}
              >
                <div
                  className="associate-info-modal__login-search-icon"
                  onClick={() => {
                    const { login } = editAssociateInfoModalFormData;
                    dispatch(getEmployeeInfo(login));
                    dispatch(fetchAustinCases({ login, placementId: +placementId }));
                  }}
                >
                  <Icon tokens={searchTokens}></Icon>
                </div>
              </Input>
            </Row>
            <Row widths={['grid-3', 'grid-8']}>
              <Text tag="label" type="b300" color="primary">
                Full name
              </Text>
              <Input
                disabled={true}
                type="text"
                placeholder="Associate's full name"
                value={editAssociateInfoModalFormData.fullName}
                onChange={(newFullName) => handleChange('fullName', newFullName)}
              ></Input>
            </Row>
            <Row widths={['grid-3', 'grid-8']}>
              <Text tag="label" type="b300" color="primary">
                Employee ID
              </Text>
              <Input
                disabled={true}
                type="text"
                placeholder="Associate's employee ID"
                value={editAssociateInfoModalFormData.employeeId}
                onChange={(newEmployeeId) => handleChange('employeeId', newEmployeeId)}
              ></Input>
            </Row>
            <Row widths={['grid-3', 'grid-8']}>
              <Text tag="label" type="b300" color="primary">
                Job
              </Text>
              <Input
                disabled={true}
                type="text"
                placeholder="Associate's assigned job"
                value={editAssociateInfoModalFormData.job}
                onChange={(newJob) => handleChange('job', newJob)}
              ></Input>
            </Row>
            <Row widths={['grid-3', 'grid-8']}>
              <Text tag="label" type="b300" color="primary">
                Shift
              </Text>
              <Input
                type="text"
                placeholder="Shift Code (i.e. DCA-0730)"
                value={editAssociateInfoModalFormData.homeShift}
                onChange={(newShift) => handleChange('homeShift', newShift)}
              ></Input>
            </Row>
            <Row widths={['grid-3', 'grid-8']}>
              <Text tag="label" type="b300" color="primary">
                Case Source
              </Text>
              <ButtonGroup
                value={editAssociateInfoModalFormData.caseSource}
                onChange={(newCaseSource) => {
                  handleChange('caseSource', newCaseSource);
                }}
              >
                <ButtonOption value="AUSTIN">AUSTIN</ButtonOption>
                <BaliOrDaliOption
                  disabled={false}
                  baliOrDali={
                    placementDetails.countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID
                      ? CaseSources.Bali
                      : CaseSources.Dali
                  }
                ></BaliOrDaliOption>
              </ButtonGroup>
            </Row>
            {(editAssociateInfoModalFormData.caseSource === CaseSources.Dali ||
              editAssociateInfoModalFormData.caseSource === CaseSources.Bali ||
              manuallyEnterCaseId) && (
              <Row widths={['grid-3', 'grid-8']}>
                <Text tag="label" type="b300" color="primary">
                  Case ID
                </Text>
                <Input
                  type="text"
                  placeholder={getExampleCaseIdText(editAssociateInfoModalFormData.caseSource)}
                  value={editAssociateInfoModalFormData.caseId}
                  onChange={(newCaseId) => handleChange('caseId', newCaseId)}
                ></Input>
              </Row>
            )}
            {editAssociateInfoModalFormData.caseSource === CaseSources.Austin && (
              <Column>
                {!manuallyEnterCaseId && (
                  <Column>
                    <Row>
                      <Text>
                        Click <Link onClick={toggleManuallyEnterCaseId}>here</Link> to manually enter the Case ID
                      </Text>
                    </Row>
                    <Row>
                      <Text>AUSTIN cases associated with {editAssociateInfoModalFormData.fullName.split(' ')[0]}</Text>
                    </Row>
                    {austinCases?.map((austinCase) => (
                      <CaseSelection
                        austinCase={austinCase}
                        handleChange={() => handleChange('caseId', austinCase.caseId)}
                        selectedCaseId={editAssociateInfoModalFormData.caseId}
                        currentAccommodationId={placementId}
                        key={austinCase.caseId}
                      />
                    ))}
                  </Column>
                )}
                {manuallyEnterCaseId && (
                  <Row>
                    <Text>
                      Click <Link onClick={toggleManuallyEnterCaseId}>here</Link> to select from a list of cases.
                    </Text>
                  </Row>
                )}
              </Column>
            )}
          </>
        ) : (
          <LoadingSpinner />
        )}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={() => dispatch(setIsEditAssociateInfoModalOpen(false))}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={saveAndClose}
            disabled={isAnyFormDataAbsent() || isEditAssociateInfoSaving}
          >
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default EditAssociateInfoModalAUSTIN;
